import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * Production runs.
 *
 * The business bottles in small runs and sells them out. The number that
 * matters on this screen is `available` — what the storefront will still
 * take money for — and the reason it is worth showing prominently is that
 * checkout is hard-gated on it: when it reaches zero the preorder button
 * disappears from the site.
 *
 * A run is planned before it is bottled, because the site sells months ahead.
 * So "capacity" is the plan until the run comes off the line and the real
 * yield replaces it. Both numbers are shown: a plan that missed is worth
 * seeing, and it is the moment a shortfall becomes visible.
 */

interface Batch {
  id: string;
  batchNumber: string;
  productId: string;
  productSize: '750ml' | '375ml';
  status: 'planned' | 'bottled' | 'closed';
  isOpen: boolean;
  plannedUnits: number;
  bottledUnits?: number;
  capacityUnits: number;
  allocatedUnits: number;
  fulfilledUnits: number;
  availableUnits: number;
  isSellable: boolean;
  shipsIn?: string;
  notes?: string;
  productionDate?: string;
  bestBy?: string;
  createdAt: string;
}

const SIZES: Batch['productSize'][] = ['750ml', '375ml'];

const BatchManagement: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    batchNumber: '', productId: '', productSize: '750ml', plannedUnits: '', shipsIn: '',
  });

  // Which run's "record the yield" field is open, and what has been typed.
  // Opened rather than acting on one click: bottling replaces the number the
  // storefront sells against, so the operator gets to see it before it lands.
  const [bottling, setBottling] = useState<string | null>(null);
  const [bottledUnits, setBottledUnits] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiService.getBatches();
      if (result.success) {
        setBatches(((result as { data?: { batches: Batch[] } }).data?.batches) ?? []);
      } else {
        setError(result.message || 'Could not load production runs');
      }
    } catch (err) {
      console.error('Batches failed to load:', err);
      setError('Could not reach the server for production runs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy('create');
    setNotice(null);
    setError(null);
    try {
      const result = await apiService.createBatch({
        batchNumber: form.batchNumber.trim(),
        productId: form.productId.trim(),
        productSize: form.productSize,
        plannedUnits: Number(form.plannedUnits),
        shipsIn: form.shipsIn.trim() || undefined,
      });

      if (result.success) {
        setNotice(`Batch ${form.batchNumber} planned.`);
        setForm({ batchNumber: '', productId: '', productSize: '750ml', plannedUnits: '', shipsIn: '' });
        setCreating(false);
        await load();
      } else {
        setError(result.message || 'Could not create the run');
      }
    } finally {
      setBusy(null);
    }
  };

  const recordBottling = async (batch: Batch) => {
    setBusy(batch.id);
    setNotice(null);
    setError(null);
    try {
      const result = await apiService.recordBatchBottling(batch.id, Number(bottledUnits));
      if (result.success) {
        setNotice(`Batch ${batch.batchNumber} bottled: ${bottledUnits} units.`);
        setBottling(null);
        setBottledUnits('');
        await load();
      } else {
        // The refusal worth reading: a yield below what is already sold is a
        // shortfall needing a decision, not a number to overwrite.
        setError(result.message || 'Could not record the yield');
      }
    } finally {
      setBusy(null);
    }
  };

  const setOpen = async (batch: Batch, isOpen: boolean) => {
    setBusy(batch.id);
    try {
      const result = await apiService.updateBatch(batch.id, { isOpen });
      if (result.success) {
        setNotice(`Batch ${batch.batchNumber} ${isOpen ? 'open for sale' : 'closed to new orders'}.`);
        await load();
      } else {
        setError(result.message || 'Could not change the run');
      }
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) return <p className="text-ink-muted">Loading production runs…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-ink">Production runs</h2>
          <p className="text-ink-muted">
            Checkout is gated on what is left. A run at zero takes the preorder button off the site.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={load} className="btn-secondary">Refresh</button>
          <button type="button" onClick={() => setCreating((v) => !v)} className="btn-primary">
            {creating ? 'Cancel' : 'Plan a run'}
          </button>
        </div>
      </div>

      {notice && <div className="rounded-lg border border-border-subtle bg-surface p-3 text-ink">{notice}</div>}
      {error && <div className="rounded-lg border border-brand-red/40 bg-surface p-3 text-brand-red">{error}</div>}

      {creating && (
        <form onSubmit={create} className="rounded-lg border border-border-subtle bg-surface p-5 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm text-ink-muted">Batch number</span>
              <input
                required
                value={form.batchNumber}
                onChange={(e) => setForm({ ...form, batchNumber: e.target.value })}
                placeholder="2026-001"
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
              />
            </label>
            <label className="block">
              <span className="text-sm text-ink-muted">Stripe product id</span>
              <input
                required
                value={form.productId}
                onChange={(e) => setForm({ ...form, productId: e.target.value })}
                placeholder="prod_…"
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
              />
            </label>
            <label className="block">
              <span className="text-sm text-ink-muted">Size</span>
              <select
                value={form.productSize}
                onChange={(e) => setForm({ ...form, productSize: e.target.value })}
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
              >
                {SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-ink-muted">Planned bottles</span>
              <input
                required
                type="number"
                min={1}
                value={form.plannedUnits}
                onChange={(e) => setForm({ ...form, plannedUnits: e.target.value })}
                placeholder="240"
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm text-ink-muted">Ships in (shown to buyers)</span>
              <input
                value={form.shipsIn}
                onChange={(e) => setForm({ ...form, shipsIn: e.target.value })}
                placeholder="October 2026"
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
              />
            </label>
          </div>
          <p className="text-sm text-ink-muted">
            The planned number is what the site sells against until the run is bottled and counted.
          </p>
          <button type="submit" disabled={busy === 'create'} className="btn-primary disabled:opacity-50">
            {busy === 'create' ? 'Planning…' : 'Plan the run'}
          </button>
        </form>
      )}

      {batches.length === 0 ? (
        <div className="rounded-lg border border-border-subtle bg-surface p-8 text-center">
          <p className="text-ink-muted">
            No runs yet. Until a product has one, it sells without a limit.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map((batch) => (
            <div key={batch.id} className="rounded-lg border border-border-subtle bg-surface p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-lg font-semibold text-ink">
                    {batch.batchNumber}
                    <span className="ml-2 text-sm font-normal text-ink-muted">
                      {batch.productSize} · {batch.status}
                      {!batch.isOpen && ' · closed'}
                    </span>
                  </p>
                  <p className="text-sm text-ink-muted">
                    {batch.productId}{batch.shipsIn ? ` · ships ${batch.shipsIn}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-semibold ${batch.availableUnits === 0 ? 'text-brand-red' : 'text-ink'}`}>
                    {batch.availableUnits}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-ink-muted">available</p>
                </div>
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
                <div><dt className="text-ink-muted">Planned</dt><dd className="text-ink">{batch.plannedUnits}</dd></div>
                <div>
                  <dt className="text-ink-muted">Bottled</dt>
                  <dd className="text-ink">{batch.bottledUnits ?? '—'}</dd>
                </div>
                <div><dt className="text-ink-muted">Sold or reserved</dt><dd className="text-ink">{batch.allocatedUnits}</dd></div>
                <div><dt className="text-ink-muted">Paid for</dt><dd className="text-ink">{batch.fulfilledUnits}</dd></div>
              </dl>

              {/* The gap between reserved and paid is abandoned checkouts still
                  holding bottles. They come back when Stripe expires them. */}
              {batch.allocatedUnits > batch.fulfilledUnits && (
                <p className="mt-3 text-sm text-ink-muted">
                  {batch.allocatedUnits - batch.fulfilledUnits} reserved by checkouts that have not been paid.
                  Stripe releases them when the session expires.
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {batch.status === 'planned' && (
                  bottling === batch.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        value={bottledUnits}
                        onChange={(e) => setBottledUnits(e.target.value)}
                        placeholder="Bottles counted"
                        aria-label={`Bottles counted for ${batch.batchNumber}`}
                        className="w-40 rounded-lg border border-border-subtle bg-surface px-3 py-2 text-ink"
                      />
                      <button
                        type="button"
                        onClick={() => recordBottling(batch)}
                        disabled={busy === batch.id || !bottledUnits}
                        className="btn-primary disabled:opacity-50"
                      >
                        Record yield
                      </button>
                      <button type="button" onClick={() => setBottling(null)} className="btn-secondary">Cancel</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setBottling(batch.id); setBottledUnits(''); }}
                      className="btn-secondary"
                    >
                      Record the yield
                    </button>
                  )
                )}

                <button
                  type="button"
                  onClick={() => setOpen(batch, !batch.isOpen)}
                  disabled={busy === batch.id}
                  className="btn-secondary disabled:opacity-50"
                >
                  {batch.isOpen ? 'Close to new orders' : 'Open for sale'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BatchManagement;
